import { CountUp } from "@/components/motion/CountUp";
import { Reveal } from "@/components/motion/Reveal";
import { COMPLETED_PROJECTS, FOUNDED_AT } from "@/data/company";
import { livePortfolio } from "@/data/portfolio";
import { fullYearsSince } from "@/lib/company";
import { usePortfolio } from "@/lib/portfolio";
import type { PortfolioItem } from "@/types/portfolio";

/**
 * 숫자는 화면에 손으로 적지 않고 전부 데이터에서 끌어와요 — 여러 곳에 적어두면 금방 어긋나요.
 * 자사 서비스 개수는 포트폴리오(DB, 어드민 → 포트폴리오 탭)에서, 설립 연차는 설립일에서
 * 파생되고, 프로젝트 건수만 `company.ts` 가 원본이에요
 * (포트폴리오엔 자사 제품만 실려 있어서 파생할 수가 없어요).
 */
const stats = (portfolio: PortfolioItem[]) => [
  {
    value: COMPLETED_PROJECTS,
    unit: "개+",
    label: "MVP 구현에서 끝나지 않고 배포·운영까지 마친 프로젝트",
  },
  {
    value: livePortfolio(portfolio).length,
    unit: "개",
    label: "직접 만들어 지금도 운영 중인 자사 서비스",
  },
  {
    value: fullYearsSince(FOUNDED_AT),
    unit: "년",
    label: `${FOUNDED_AT.slice(0, 4)}년 설립 이후 이어온 개발·운영`,
  },
];

const STRENGTHS = [
  `${COMPLETED_PROJECTS}개가 넘는 프로젝트를 배포와 운영까지 끝내봤어요. 데모까지만 만들고 멈추는 개발이 아니에요.`,
  "웹과 앱을 함께 다루는 풀스택 역량으로 하나의 서비스를 플랫폼 전반에 걸쳐 일관되게 구현해요.",
  "직접 만든 제품을 지금도 운영하며 얻은 실전 경험을, 맡겨주신 프로젝트에 그대로 씁니다.",
  "네이버와 카이스트를 거친 개발자들이 팀을 이뤄, 큰 서비스에서 쓰던 기준 그대로 기획부터 배포·운영까지 함께해요.",
];

export function About() {
  const STATS = stats(usePortfolio());

  return (
    <section id="about" className="scroll-mt-20 px-6 py-24">
      <div className="mx-auto flex max-w-layout flex-col gap-12">
        <Reveal className="flex flex-col gap-3">
          {/* 섹션 머리에 얹는 짧은 강조선. 스크롤하며 섹션이 바뀐 걸 글자보다 먼저 알려줘요. */}
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-primary" />
          <h2 className="wds-title-1 text-label-strong">의뢰받아 만들고, 운영까지 맡습니다</h2>
        </Reveal>

        <Reveal delay={0.05} className="flex flex-col gap-4">
          <p className="wds-body-1-reading text-label-neutral">
            디체인커넥트가 하는 일은 고객사의 웹과 앱을 의뢰받아 만들고, 만든 뒤의 운영까지 맡아
            드리는 거예요. 지금까지 {COMPLETED_PROJECTS}개가 넘는 프로젝트를 데모나 MVP 에서 멈추지
            않고 <span className="font-semibold text-label-strong">실제 배포와 서비스 운영까지</span>{" "}
            끝냈어요. 기획과 개발로 끝내고 손을 떼는 대신, 배포·모니터링·개선까지 이어서 책임지는
            방식으로 일해요.
          </p>
          <p className="wds-body-1-reading text-label-neutral">
            그렇게 말할 수 있는 근거는 저희가 직접 만든 제품을 지금도 운영하고 있기 때문이에요.
            2022년 설립 이후 여러 종의 자사 제품을 만들어 출시로 끝내지 않고 계속 운영해 왔고,
            사용자가 겪는 문제와 서버가 새벽에 내는 소리를 저희가 직접 받아왔어요.
          </p>
          <p className="wds-body-1-reading text-label-neutral">
            그 경험을 그대로 고객사 프로젝트에 씁니다.{" "}
            <span className="font-semibold text-label-strong">네이버와 카이스트를 거친 개발자들이</span>{" "}
            요구사항 정리부터 개발, 배포, 운영까지 한 팀으로 맡기 때문에 만드는 사람과 운영하는
            사람이 달라서 생기는 공백이 없어요.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          {/* 항목 사이 세로 구분선은 sm 이상에서만 — 한 칸씩 세로로 쌓이는 모바일에선 선이 옆구리에 떠요. */}
          <dl className="grid gap-6 border-line-alternative border-y py-8 sm:grid-cols-3 sm:gap-0">
            {STATS.map((stat, i) => (
              <div
                key={stat.label}
                className={`flex flex-col gap-1 sm:px-8 ${i > 0 ? "sm:border-line-alternative sm:border-l" : "sm:pl-0"}`}
              >
                <dt className="flex items-baseline gap-0.5">
                  <CountUp value={stat.value} className="wds-display-1 font-bold text-primary" />
                  <span className="wds-headline-1 font-bold text-primary">{stat.unit}</span>
                </dt>
                <dd className="wds-caption-1 text-label-neutral">{stat.label}</dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <Reveal delay={0.1}>
          <ul className="grid gap-4 sm:grid-cols-2">
            {STRENGTHS.map((s, i) => (
              <li
                key={s}
                className="flex gap-3 rounded-lg bg-background-alternative p-5 transition-shadow duration-200 ease-standard hover:shadow-normal"
              >
                {/* 번호를 매기면 네 개가 목록으로 읽혀요 — 가운뎃점은 어디까지가 한 항목인지 덜 보여요. */}
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary wds-caption-1 font-bold text-static-white tabular-nums">
                  {i + 1}
                </span>
                <span className="wds-body-2-reading text-label-neutral">{s}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

export default About;
